import { useMemo, useState } from 'react';
import { Download, Table2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { cn } from '@/shared/lib/cn';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { downloadXlsx, fechaArchivoHoy } from '@/shared/lib/xlsx';
import {
  useVentasDetalle,
  type RangoPreset,
  type VentaLineaDetalle,
  type VentasDashboard,
} from './ventasApi';
import { fmtARS, fmtARSCompact, fmtInt } from './dashFormat';
import { CAT_PALETTE, StackedBarsChart, type StackSeries } from './ventasCharts';
import { SegBar } from './dashUi';

const TODOS = 'Todos';
const OTROS_COLOR = '#898781';
const TOP_N = 8;

export function VentasPorTipoCard({ data, rango }: { data: VentasDashboard; rango: RangoPreset }) {
  const [tipo, setTipo] = useState<string>(TODOS);
  const [medida, setMedida] = useState<'monto' | 'unidades'>('monto');
  const [detalleOpen, setDetalleOpen] = useState(false);

  const tipos = useMemo(() => [TODOS, ...data.tiposVendidos.map((t) => t.tipo)], [data.tiposVendidos]);
  const labels = data.series.map((s) => s.label);

  // Artículos del tipo elegido → top 8 + "Otros", como series apiladas.
  const series: StackSeries[] = useMemo(() => {
    const arts = tipo === TODOS ? data.articulosMes : data.articulosMes.filter((a) => a.tipo === tipo);
    const pick = (a: (typeof arts)[number]) => (medida === 'monto' ? a.montoSerie : a.unidadesSerie);
    const top = arts.slice(0, TOP_N);
    const rest = arts.slice(TOP_N);
    const out: StackSeries[] = top.map((a, i) => ({
      key: a.articulo.toUpperCase(),
      color: CAT_PALETTE[i % CAT_PALETTE.length],
      values: pick(a),
    }));
    if (rest.length > 0) {
      const otros = labels.map((_, i) => rest.reduce((s, a) => s + (pick(a)[i] || 0), 0));
      out.push({ key: `Otros (${rest.length})`, color: OTROS_COLOR, values: otros });
    }
    return out;
  }, [data.articulosMes, tipo, medida, labels]);

  const fmtValue = medida === 'monto' ? fmtARS : (n: number) => `${fmtInt(n)} u`;
  const fmtCompact = medida === 'monto' ? fmtARSCompact : (n: number) => fmtInt(n);

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 pb-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="text-base">Ventas por tipo y artículo (por mes de pago)</CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setDetalleOpen(true)}>
              <Table2 className="mr-2 h-4 w-4" aria-hidden />
              Ver detalle
            </Button>
            <SegBar
              value={medida}
              onChange={setMedida}
              options={[
                { id: 'monto', label: '$' },
                { id: 'unidades', label: 'Q' },
              ]}
            />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1">
          {tipos.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTipo(t)}
              className={cn(
                'rounded-md px-2.5 py-1 text-sm font-medium transition-colors',
                tipo === t ? 'bg-foreground text-background' : 'text-muted-foreground hover:bg-muted',
              )}
            >
              {t}
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {series.length === 0 ? (
          <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
            Sin ventas de este tipo en el período.
          </div>
        ) : (
          <>
            <StackedBarsChart labels={labels} series={series} formatValue={fmtValue} formatCompact={fmtCompact} />
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
              {series.map((s) => (
                <span key={s.key} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                  <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm" style={{ backgroundColor: s.color }} />
                  <span className="max-w-[14rem] truncate">{s.key}</span>
                </span>
              ))}
            </div>
          </>
        )}
      </CardContent>

      <DetalleDialog open={detalleOpen} onOpenChange={setDetalleOpen} rango={rango} tipo={tipo} />
    </Card>
  );
}

function DetalleDialog({
  open,
  onOpenChange,
  rango,
  tipo,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  rango: RangoPreset;
  tipo: string;
}) {
  const q = useVentasDetalle(rango, open);
  const lineas = useMemo(() => {
    const all = q.data?.lineas ?? [];
    return tipo === TODOS ? all : all.filter((l) => l.tipo === tipo);
  }, [q.data, tipo]);

  const totalU = lineas.reduce((s, l) => s + l.unidades, 0);
  const totalM = lineas.reduce((s, l) => s + l.monto, 0);

  const exportar = () => {
    void downloadXlsx({
      filename: `ventas_detalle_${fechaArchivoHoy()}`,
      sheetName: 'Ventas detalle',
      headers: ['Fecha pago', 'Mes-Año', 'Fecha cotización', 'Artículo', 'Tipo', 'Cliente', 'Vendedor', 'Unidades', 'Monto'],
      rows: lineas.map((l) => [
        l.fechaPago, l.mesAnio, l.fecha, l.articulo.toUpperCase(), l.tipo, l.cliente, l.vendedor, l.unidades, Math.round(l.monto),
      ]),
      totals: [
        { label: 'Total unidades', value: totalU },
        { label: 'Total monto', value: Math.round(totalM) },
      ],
    });
  };

  const cols: Array<{ label: string; width: string; get: (l: VentaLineaDetalle) => string; num?: boolean; emphasis?: boolean }> = [
    { label: 'Fecha pago', width: '10%', get: (l) => l.fechaPago || '—' },
    { label: 'Mes-Año', width: '9%', get: (l) => l.mesAnio },
    { label: 'Artículo', width: '25%', get: (l) => l.articulo.toUpperCase() },
    { label: 'Tipo', width: '11%', get: (l) => l.tipo },
    { label: 'Cliente', width: '19%', get: (l) => l.cliente },
    { label: 'Unid.', width: '8%', get: (l) => fmtInt(l.unidades), num: true },
    { label: 'Monto', width: '13%', get: (l) => fmtARS(l.monto), num: true, emphasis: true },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[min(98vw,72rem)]">
        <DialogHeader className="flex flex-row items-center justify-between gap-3 pr-8">
          <DialogTitle>
            Detalle de ventas{tipo !== TODOS ? ` · ${tipo}` : ''}{' '}
            {q.data ? `(${fmtInt(lineas.length)} líneas)` : ''}
          </DialogTitle>
          <Button variant="outline" size="sm" onClick={exportar} disabled={!q.data || lineas.length === 0}>
            <Download className="mr-2 h-4 w-4" aria-hidden />
            Descargar XLS
          </Button>
        </DialogHeader>
        <DialogBody className="max-h-[72vh] overflow-y-auto overflow-x-hidden px-0">
          {q.isLoading ? (
            <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">Cargando…</div>
          ) : lineas.length === 0 ? (
            <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
              Sin ventas en el período.
            </div>
          ) : (
            <table className="w-full table-fixed border-collapse text-sm">
              <colgroup>
                {cols.map((c) => (
                  <col key={c.label} style={{ width: c.width }} />
                ))}
              </colgroup>
              <thead className="sticky top-0 z-10 bg-card">
                <tr className="border-b">
                  {cols.map((c) => (
                    <th
                      key={c.label}
                      className={cn(
                        'px-3 py-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground',
                        c.num ? 'text-right' : 'text-left',
                      )}
                    >
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lineas.map((l, i) => (
                  <tr key={i} className={cn(i % 2 === 1 && 'bg-muted/30', 'hover:bg-muted/60')}>
                    {cols.map((c) => (
                      <td
                        key={c.label}
                        title={c.label === 'Artículo' ? l.articulo.toUpperCase() : c.label === 'Cliente' ? l.cliente : undefined}
                        className={cn(
                          'overflow-hidden text-ellipsis whitespace-nowrap px-3 py-1.5',
                          c.num ? 'text-right tabular-nums' : 'text-left',
                          c.label === 'Artículo' || c.emphasis ? 'font-medium text-foreground' : 'text-muted-foreground',
                        )}
                      >
                        {c.get(l)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
